import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, ArrowLeft, Check, Loader2, Calendar as CalIcon, Home as HomeIcon, Building2, Droplets, Layers, CloudRain } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/site/SEO";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

const iconMap = { home: HomeIcon, "building-2": Building2, droplets: Droplets, layers: Layers, "cloud-rain": CloudRain };

const STEPS = ["Service", "Property", "Date & Time", "Contact"];

export default function Book() {
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [serviceIds, setServiceIds] = useState([]);  // multi-select: array of service ids
  const [propertyType, setPropertyType] = useState("residential");
  const [sqft, setSqft] = useState("");
  const [address, setAddress] = useState("");
  const [zip, setZip] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotIso, setSlotIso] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "", consent_sms: true, consent_email: true, honeypot: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/services").then(r => setServices(r.data)); }, []);

  const selectedServices = useMemo(
    () => serviceIds.map(id => services.find(s => s.id === id)).filter(Boolean),
    [services, serviceIds],
  );
  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (Number(s.base_price) || 0), 0),
    [selectedServices],
  );
  const totalDuration = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (Number(s.duration_minutes) || 0), 0),
    [selectedServices],
  );
  const depositAmount = totalPrice / 2;

  const toggleService = (id) => setServiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const serviceIdsParam = useMemo(() => serviceIds.join(","), [serviceIds]);
  const fetchSlots = useCallback(async (d) => {
    if (!d) return;
    setLoadingSlots(true);
    try {
      const params = { date: d };
      if (serviceIdsParam) params.service_ids = serviceIdsParam;
      if (totalDuration) params.duration = totalDuration;
      const { data } = await api.get("/booking/availability", { params });
      setSlots(data.slots || []);
    } finally { setLoadingSlots(false); }
  }, [serviceIdsParam, totalDuration]);

  // Re-fetch whenever the date or the requested span (service selection / duration) changes,
  // so a multi-hour combo re-computes which slots actually fit.
  useEffect(() => {
    if (!dateStr) return;
    setSlotIso("");
    fetchSlots(dateStr);
  }, [dateStr, fetchSlots]);

  const canNext = () => {
    if (step === 0) return serviceIds.length > 0;
    if (step === 1) return !!address && !!zip;
    if (step === 2) return !!slotIso;
    if (step === 3) return !!form.first_name && !!form.last_name && !!form.email && !!form.phone;
    return false;
  };

  const next = () => setStep(s => Math.min(s + 1, 3));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_ids: serviceIds,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        address,
        zip,
        city: "Eugene",
        state: "OR",
        property_type: propertyType,
        stories: Number(sqft || 1),
        scheduled_at: slotIso,
        notes: form.notes + (sqft ? `\nApprox sq ft: ${sqft}` : ""),
        consent_sms: form.consent_sms,
        consent_email: form.consent_email,
        honeypot: form.honeypot,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/booking", payload);
      if (data.checkout_url) {
        toast.success("Redirecting to secure payment…");
        window.location.href = data.checkout_url;
        return;
      }
      // Honeypot spam short-circuit (server returns {ok:true} silently)
      toast.success("Thanks — we'll be in touch.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO title="Book Online — Wonder Washers Pressure Washing" description="Book pressure washing online for house washing, driveways, decks, roofs, and commercial buildings across Oregon." canonical="[BOOKING_URL]/book" />
      <section className="max-w-3xl mx-auto py-12 md:py-16 px-4 md:px-6">
        <div className="text-center mb-8">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase mb-3">Book Online</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl text-[color:var(--ww-navy)] leading-none">Four steps.<br /> Two minutes.</h1>
        </div>

        <div className="ww-card p-4 md:p-5 mb-6" data-testid={T.booking.stepIndicator}>
          <div className="flex items-center gap-2 mb-3">
            {STEPS.map((label, i) => (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-2 ${i <= step ? "text-[color:var(--ww-navy)]" : "text-slate-400"}`}>
                  <span className={`h-7 w-7 rounded-md flex items-center justify-center text-xs font-bold ${i < step ? "bg-[color:var(--ww-gold)] text-[color:var(--ww-navy)]" : i === step ? "bg-[color:var(--ww-red)] text-white" : "bg-slate-200 text-slate-500"}`}>
                    {i < step ? <Check className="h-4 w-4" /> : i + 1}
                  </span>
                  <span className="text-xs font-subhead uppercase tracking-wider hidden sm:block">{label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-1 rounded-full ${i < step ? "bg-[color:var(--ww-gold)]" : "bg-slate-200"}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="ww-card-lg p-6 md:p-8">
          {step === 0 && (
            <div>
              <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)] mb-2">Which services?</h2>
              <p className="text-slate-600 mb-6">Pick one or more — the deposit and invoice tally automatically.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {services.map((s) => {
                  const Icon = iconMap[s.icon] || Droplets;
                  const active = serviceIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      data-testid={T.booking.serviceCard(s.slug)}
                      aria-pressed={active}
                      onClick={() => toggleService(s.id)}
                      className={`text-left border-2 rounded-md p-4 transition-all ${active ? "border-[color:var(--ww-red)] bg-red-50/40 shadow-sm" : "border-slate-200 hover:border-[color:var(--ww-pressure-blue)]"}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${active ? "bg-[color:var(--ww-red)] text-white" : "bg-slate-100 text-[color:var(--ww-navy)]"}`}>
                          {active ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                        </span>
                        <div className="flex-1">
                          <div className="font-display uppercase text-lg text-[color:var(--ww-navy)] leading-tight">{s.name}</div>
                          <div className="text-xs text-slate-500 mt-1">~{s.duration_minutes}min · from ${s.base_price?.toFixed?.(0)}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              {serviceIds.length > 0 && (
                <div className="mt-6 ww-card p-4 bg-slate-50 flex items-center justify-between" data-testid="booking-services-subtotal">
                  <div>
                    <div className="text-xs font-subhead tracking-widest text-slate-500 uppercase">Selected ({serviceIds.length})</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {selectedServices.map(s => s.name).join(" + ")} · ~{totalDuration}min total
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-subhead tracking-widest text-slate-500 uppercase">Est. total</div>
                    <div className="font-display text-3xl text-[color:var(--ww-navy)]">${totalPrice.toFixed(0)}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)] mb-2">Where's the property?</h2>
              <p className="text-slate-600 mb-6">We currently serve the Eugene/Springfield area (25-mile radius from 97401).</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Street address</label>
                  <input data-testid={T.booking.address} className="ww-input mt-1" placeholder="1428 Kincaid St" value={address} onChange={e => setAddress(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">ZIP code</label>
                  <input data-testid={T.booking.zip} className="ww-input mt-1" placeholder="97401" value={zip} onChange={e => setZip(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Property type</label>
                  <select className="ww-input mt-1" value={propertyType} onChange={e => setPropertyType(e.target.value)}>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Approx. sq ft (optional)</label>
                  <input className="ww-input mt-1" placeholder="e.g. 2400" value={sqft} onChange={e => setSqft(e.target.value)} />
                </div>
              </div>
              {zip && !zip.startsWith("974") && (
                <div className="mt-4 ww-badge-amber inline-flex">Outside our usual 25-mile radius — we'll confirm availability.</div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)] mb-2">Pick a time</h2>
              <p className="text-slate-600 mb-6">We work Mon–Sat, 8am–5pm. Arrival window is 30 min.</p>
              <div className="flex items-center gap-3 mb-6">
                <CalIcon className="h-5 w-5 text-[color:var(--ww-pressure-blue)]" />
                <input
                  data-testid={T.booking.dateInput}
                  type="date"
                  className="ww-input"
                  min={new Date().toISOString().slice(0, 10)}
                  value={dateStr}
                  onChange={e => { setDateStr(e.target.value); setSlotIso(""); }}
                />
              </div>
              {totalDuration > 0 && (
                <div
                  data-testid="booking-duration-hint"
                  className="mb-3 text-xs font-subhead uppercase tracking-widest text-slate-500"
                >
                  Requires {Math.floor(totalDuration / 60)}h{totalDuration % 60 ? ` ${totalDuration % 60}m` : ""} on-site — slots that don't fit are greyed out
                </div>
              )}
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading times…</div>
              ) : dateStr && slots.length === 0 ? (
                <div className="ww-badge-amber">Closed on that day — pick another.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {slots.map((s) => (
                    <button
                      key={s.iso}
                      data-testid={T.booking.slot(s.iso)}
                      disabled={!s.available}
                      onClick={() => setSlotIso(s.iso)}
                      className={`py-2.5 rounded-md border-2 text-sm font-subhead uppercase tracking-wider transition-all ${
                        slotIso === s.iso ? "border-[color:var(--ww-red)] bg-red-50 text-[color:var(--ww-navy)]" :
                        !s.available ? "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed line-through" :
                        "border-slate-200 hover:border-[color:var(--ww-pressure-blue)] text-[color:var(--ww-navy)]"
                      }`}
                    >
                      {formatTime(s.time || s.iso)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display uppercase text-3xl text-[color:var(--ww-navy)] mb-2">Your details</h2>
              <p className="text-slate-600 mb-6">Last step — we'll send confirmation to both.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">First name *</label>
                  <input data-testid={T.booking.firstName} required autoComplete="given-name" className="ww-input mt-1" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Last name *</label>
                  <input data-testid={T.booking.lastName} required autoComplete="family-name" className="ww-input mt-1" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Email *</label>
                  <input data-testid={T.booking.email} required type="email" autoComplete="email" className="ww-input mt-1" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Mobile number *</label>
                  <input data-testid={T.booking.phone} required type="tel" inputMode="tel" autoComplete="tel" className="ww-input mt-1" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(541) 555-0117" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Any prep notes?</label>
                  <textarea className="ww-input mt-1 min-h-[90px]" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Pets, gate codes, exterior spigots, delicate landscaping…" />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                    <input data-testid={T.booking.consentSms} type="checkbox" className="mt-1" checked={form.consent_sms} onChange={e => setForm(f => ({ ...f, consent_sms: e.target.checked }))} />
                    <span>Send me appointment texts from Wonder Washers (confirmations + weather-aware reminders). Reply STOP anytime. Msg &amp; data rates may apply.</span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                    <input data-testid={T.booking.consentEmail} type="checkbox" className="mt-1" checked={form.consent_email} onChange={e => setForm(f => ({ ...f, consent_email: e.target.checked }))} />
                    <span>Send me email confirmations, before/after photos, and receipts.</span>
                  </label>
                </div>
                <input type="text" tabIndex={-1} autoComplete="off" data-testid={T.booking.honeypot}
                  className="absolute -left-[9999px] w-0 h-0 opacity-0" value={form.honeypot}
                  onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))} />
              </div>

              <div className="mt-6 ww-card p-4 bg-slate-50">
                <div className="text-xs font-subhead tracking-widest text-slate-500 uppercase mb-3">Your invoice</div>
                <div className="text-sm text-slate-700 space-y-1" data-testid="booking-review-services">
                  {selectedServices.map(s => (
                    <div key={s.id} className="flex items-baseline justify-between">
                      <div><strong>{s.name}</strong> <span className="text-slate-500">· ~{s.duration_minutes}min</span></div>
                      <div className="font-semibold text-[color:var(--ww-navy)]">${Number(s.base_price).toFixed(2)}</div>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between pt-2 mt-2 border-t border-slate-200">
                    <div className="font-subhead uppercase tracking-widest text-xs text-slate-500">Total ({selectedServices.length} {selectedServices.length === 1 ? "service" : "services"})</div>
                    <div className="font-display text-2xl text-[color:var(--ww-navy)]" data-testid="booking-review-total">${totalPrice.toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-3 text-sm text-slate-700">
                  <div>{address}, {zip}</div>
                  <div>{formatDateLong(slotIso)}</div>
                  <div className="mt-1 text-slate-500 text-xs">Starting prices — final confirmed after property review.</div>
                </div>
                {totalPrice > 0 ? (
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid="booking-deposit-summary">
                    <div className="flex items-baseline justify-between">
                      <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-red)] uppercase">Due today (50% deposit)</div>
                      <div className="font-display text-3xl text-[color:var(--ww-navy)]" data-testid="booking-deposit-amount">${depositAmount.toFixed(2)}</div>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">Remaining ${depositAmount.toFixed(2)} balance is due after your appointment — we'll email a secure payment link.</div>
                    <div className="mt-2 text-[11px] text-slate-500">
                      <strong>Cancellation:</strong> full refund if you cancel 7+ days before your appointment. Inside 7 days, the deposit is non-refundable.
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="mt-4 text-xs text-slate-500">
                By continuing you agree to our privacy practices. We never sell your info.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button data-testid={T.booking.back} onClick={back} disabled={step === 0} className="ww-btn-outline text-sm inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          {step < 3 ? (
            <button data-testid={T.booking.next} onClick={next} disabled={!canNext()} className="ww-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button data-testid={T.booking.submit} onClick={submit} disabled={!canNext() || submitting} className="ww-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Pay deposit &amp; book
            </button>
          )}
        </div>
      </section>
    </>
  );
}

function formatTime(v) {
  // Accepts either an ISO string ("2026-08-27T08:00:00-07:00") or an "HH:MM" string.
  if (!v) return "";
  // If it's a plain HH:MM, format directly without timezone gymnastics
  if (/^\d{2}:\d{2}$/.test(v)) {
    const [hh, mm] = v.split(":").map(Number);
    const suffix = hh >= 12 ? "PM" : "AM";
    const hour12 = ((hh + 11) % 12) + 1;
    return `${hour12}:${String(mm).padStart(2, "0")} ${suffix}`;
  }
  try {
    const d = new Date(v);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZone: "America/Los_Angeles" });
  } catch { return v; }
}
function formatDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Los_Angeles" });
  } catch { return iso; }
}
